/**
 * HOW BIG THE BOARD DRAWS A PIECE (card-a5 item 13, reopened — the operator, 2026-10-11: «the chess pieces must also be
 * bigger — as large as the square comfortably allows — still in prod's exact design and colours. Check it at every
 * size, both themes.»).
 *
 * Prod draws every board piece in a `clamp(20px, 8vw, 34px)` box: sized by the VIEWPORT, not by its square. On a tall
 * phone the board is width-limited and that box is ~70 % of the square (the operator's 384×823: 30.7 px in 42.25 px);
 * on a short window the board is height-limited and the same box already fills or overflows its square.
 *
 * Now: `max(clamp(20px, 8vw, 34px), <what the square allows>)` — the SAME artwork, bigger where the square has room,
 * and never smaller than today anywhere (where today's piece already fills its square, it stays exactly as it is).
 *
 * «What the square allows» is the largest box whose ink — the 12 SVGs' own geometry, half the 1.35-unit stroke
 * included, the box centred in its square as the table draws it — keeps clear of every marker of the square:
 *
 *   - the SELECTED square's border (an inset 3 px box-shadow, painted UNDER the piece — a bigger piece would hide it):
 *     the ink stays ≥ 3 px + {@link MARKER_GAP_PX} from every edge. The king's cross top is the highest ink
 *     ({@link INK_TOP}), and the art sits 0.9 unit above its box's centre, so the top binds:
 *     `(S − 2·(3 + 1)) / (1 − 2·INK_TOP)`;
 *   - the CAPTURE ring (inset 7 %, a 3 px border, painted OVER the piece, so never hidden): the same «comfortably» —
 *     every ink ≥ {@link MARKER_GAP_PX} inside its outer edge (radius 0.43·S); the base's two rounded ends are the ink
 *     farthest from the centre ({@link INK_RADIUS}): `(0.43·S − 1) / INK_RADIUS`. The ends pass under the ring's 3 px
 *     band by at most 2 px; no ink reaches its anti-aliased outer edge or beyond (measured: 0 ink pixels there at every
 *     size that grows). Inside its INNER edge instead would allow, on the operator's phone (384×823, a 42.25 px square),
 *     29.09 px with the 1 px gap or 31.15 px without — against today's 30.72 px: no visible growth at all.
 *   - the last-move tint and the check glow fill the square under the piece and show around it at any size that
 *     clears the border; the legal-move dot is only ever on an EMPTY square.
 *   - the coordinate labels (bold 9 px at top 2 / left 3 and bottom 1 / right 3, painted OVER the piece) are not in
 *     the formula: wherever the square lets the piece grow, they are ≥ 1 px clear anyway (measured on the 20-size
 *     matrix; `chess-piece-size.test.tsx` pins it with the art's real geometry).
 *
 * The square side comes from the board's own measured size (`useFitSquare` → `fit.size / 8`), so the rule follows the
 * square on every viewport. Trays, the promotion picker and the start screen's king keep their own fixed sizes.
 */

/** Prod's (fbb8d48's) board-piece box — the floor: no piece is ever drawn smaller than this. */
export const PIECE_CLAMP = "clamp(20px, 8vw, 34px)";

/** The selected square's border: `BoardSquare`'s `boxShadow: inset 0 0 0 3px …` (painted under the piece). */
export const SELECTED_BORDER_PX = 3;
/** The gap kept between a piece's ink and a marker — the «comfortably» of the operator's words. */
export const MARKER_GAP_PX = 1;
/** The capture ring's outer radius as a fraction of the square: `inset: "7%"` → 0.5 − 0.07. */
export const CAPTURE_RING_RADIUS = 0.43;

/** The art's 45-unit box (`viewBox="0 0 45 45"`, every piece). */
const BOX_UNITS = 45;
/** The highest ink of any piece, as a fraction of its box: the king's cross top, y 2.4 − half the 1.35 stroke. */
export const INK_TOP = (2.4 - 1.35 / 2) / BOX_UNITS;
/** The ink farthest from the box centre, as a fraction of the box: the shared base's rounded ends — a 1.7-unit
 *  radius (+ half the stroke) around (12.2, 39.1) and (32.8, 39.1), the centre at (22.5, 22.5). */
export const INK_RADIUS = (Math.hypot(22.5 - 12.2, 39.1 - 22.5) + 1.7 + 1.35 / 2) / BOX_UNITS;

/** The largest box (px) a square of side `square` px allows by the rules above; 0 when it allows none. Floored to
 *  0.01 px so rounding can never push the ink onto a marker. */
export function squareAllowedPieceBox(square: number): number {
  if (!(square > 0)) return 0;
  const clearOfBorder = (square - 2 * (SELECTED_BORDER_PX + MARKER_GAP_PX)) / (1 - 2 * INK_TOP);
  const insideRing = (CAPTURE_RING_RADIUS * square - MARKER_GAP_PX) / INK_RADIUS;
  return Math.max(0, Math.floor(Math.min(clearOfBorder, insideRing) * 100) / 100);
}

/** The CSS box of a board piece (width = height) in a square of side `square` px: today's box, or what the square
 *  allows if that is bigger. Before the board is measured (`square` 0) it is today's box. */
export function boardPieceSize(square: number): string {
  const allowed = squareAllowedPieceBox(square);
  return allowed > 0 ? `max(${PIECE_CLAMP}, ${allowed}px)` : PIECE_CLAMP;
}
